import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { NewsApi } from '@/services/api/api.news';
import {
    NewsViewObject,
    NewsCommentViewObject,
    NewsReactionViewObject,
    NewsCommentReactionViewObject,
    NewsCommentAndReactionViewObject,
    NewsAddCommentRequest,
    NewsDeleteCommentRequest,
    NewsEditCommentRequest,
    NewsCreateRequest,
    NewsDeleteRequest,
    NewsEditRequest,
    NewsReactionRequest,
    NewsCommentReactionRequest,
    SpecificNewsRequest
} from '@/types/apiServerContract';

@Module({ namespaced: true, dynamic: true, store, name: 'NewsModule' })
class AdminState extends VuexModule {
	// ------------ STATE ----------- //
	news: NewsViewObject[] = [];

	// ------------ MUTATIONS ----------- //
	@Mutation
	SetNews(news: NewsViewObject[]) {
	    this.news = news;
	}

	@Mutation
	AddNews(news: NewsViewObject) {
	    this.news.unshift(news);
	}

	@Mutation
	RemoveNews(id: number) {
	    const index = this.news.findIndex(x => x.id === id);
	    this.news.splice(index, 1);
	}

	@Mutation
	UpdateNews(news: NewsViewObject) {
	    const oldNews = this.news.find(x => x.id === news.id);
	    if (!oldNews) return;
	    Object.assign(oldNews, news);
	}

	@Mutation
	UpdateComments(data: { comments: NewsCommentViewObject[]; newsId: number }) {
	    const news = this.news.find(x => x.id === data.newsId);
	    if (news) {
	        news.commentCount = data.comments.length;
	        news.comments = data.comments;
	    }
	}

	@Mutation
	UpdateComment(comment: NewsCommentViewObject) {
	    const news = this.news.find(x => x.id === comment.newsId);
	    if (!news) return;
	    const oldComment = news.comments?.find(x => x.id === comment.id);
	    if (!oldComment) return;
	    Object.assign(oldComment, comment);
	}

	@Mutation
	UpdateNewsReaction(reaction: NewsReactionViewObject) {
	    const news = this.news.find(x => x.id === reaction.newsId);
	    if (!news) return;

	    if (reaction.active) {
	        news.reactions.push(reaction);
	    } else {
	        const index = news.reactions.findIndex(x => x.newsId === reaction.newsId && x.type === reaction.type && x.author === reaction.author);
	        if (index >= 0) {
	            news.reactions.splice(index, 1);
	        }
	    }
	}

	@Mutation
	UpdateNewsCommentReaction(reaction: NewsCommentReactionViewObject) {
	    const news = this.news.find(x => x.id === reaction.newsId);
	    if (!news) return;
	    const comments = news.comments.find(x => x.id === reaction.newsCommentId);
	    if (!comments) return;

	    if (reaction.active) {
	        comments.reactions.push(reaction);
	    } else {
	        const index = comments.reactions.findIndex(x => x.newsId === reaction.newsId && x.type === reaction.type && x.author === reaction.author);
	        if (index >= 0) {
	            comments.reactions.splice(index, 1);
	        }
	    }
	}

	@Mutation
	UpdateNewsData(response: NewsCommentAndReactionViewObject) {
	    const news = this.news.find(x => x.id === response.newsId);
	    if (!news) return;
	    news.commentCount = response.commentCount;
	    news.reactions = response.reactions;
	}

	// ------------ ACTIONS ----------- //
	@Action({ rawError: true })
	async GetNews() {
	    const response = await NewsApi.GetNews();
	    this.SetNews(response);
	}

	@Action({ rawError: true })
	async AddComment(request: NewsAddCommentRequest) {
	    const response = await NewsApi.AddComment(request);
	    this.UpdateComments({ comments: response, newsId: request.newsId });
	}

	@Action({ rawError: true })
	async DeleteComment(request: NewsDeleteCommentRequest) {
	    const response = await NewsApi.DeleteComment(request);
	    this.UpdateComments({ comments: response, newsId: request.newsId });
	}

	@Action({ rawError: true })
	async EditComment(request: NewsEditCommentRequest) {
	    const response = await NewsApi.EditComment(request);
	    this.UpdateComments({ comments: response, newsId: request.newsId });
	}

	@Action({ rawError: true })
	async CreateNews(request: NewsCreateRequest): Promise<NewsViewObject> {
	    const response = await NewsApi.CreateNews(request);
	    this.AddNews(response);
	    return response;
	}

	@Action({ rawError: true })
	async DeleteNews(request: NewsDeleteRequest): Promise<void> {
	    await NewsApi.DeleteNews(request);
	    this.RemoveNews(request.newsId);
	}

	@Action({ rawError: true })
	async EditNews(request: NewsEditRequest): Promise<NewsViewObject> {
	    const response = await NewsApi.EditNews(request);
	    this.UpdateNews(response);
	    return response;
	}

	@Action({ rawError: true })
	async NewsReaction(request: NewsReactionRequest) {
	    const response = await NewsApi.NewsReaction(request);
	    this.UpdateNewsReaction(response);
	}

	@Action({ rawError: true })
	async NewsCommentReaction(request: NewsCommentReactionRequest) {
	    const response = await NewsApi.NewsCommentReaction(request);
	    this.UpdateNewsCommentReaction(response);
	}

	@Action({ rawError: true })
	async GetNewsData(request: SpecificNewsRequest) {
	    const response = await NewsApi.GetNewsData(request);
	    this.UpdateNewsData(response);
	}

	@Action({ rawError: true })
	async GetComments(request: SpecificNewsRequest) {
	    const response = await NewsApi.GetComments(request);
	    this.UpdateComments({ comments: response, newsId: request.newsId });
	}

	// ---------------------------- //
	@Mutation
	Reset() {}

	@Action({ rawError: true })
	Clear() {
	    this.Reset();
	}

	@Action({ rawError: true })
	async Init() {}
}

const NewsModule = getModule(AdminState);

export { NewsModule };
